import { Link } from "react-router-dom";
import { Phone, Check, Clock, ArrowRight } from "lucide-react";
import { SEO, localBusinessSchema } from "@/components/SEO";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { BUSINESS } from "@/data/business";
import { SERVICES } from "@/data/services";

export const ServicePageTemplate = ({ service }) => {
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug);

  return (
    <>
      <SEO
        title={service.metaTitle}
        description={service.metaDescription}
        path={service.path}
        schema={localBusinessSchema()}
      />

      {/* HERO */}
      <section
        data-testid={`service-hero-${service.slug}`}
        className="relative h-[70vh] min-h-[480px] flex items-end overflow-hidden"
      >
        <img
          src={service.heroImage}
          alt={`${service.name} ambient treatment setup at Blossom Spa Madhapur`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-spa-slate via-spa-slate/50 to-spa-slate/10" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-20 w-full">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-spa-blush mb-5">
              Blossom Spa Madhapur
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-spa-cream max-w-2xl">
              {service.name}
            </h1>
            <p className="mt-4 text-spa-cream/80 max-w-lg text-base sm:text-lg">
              {service.tagline}
            </p>
          </Reveal>
        </div>
      </section>

      {/* OVERVIEW + BENEFITS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-3">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-spa-terracotta mb-4">
            Overview
          </p>
          <p className="font-serif text-2xl sm:text-3xl text-spa-slate leading-snug">
            {service.description}
          </p>
        </Reveal>

        <Reveal className="lg:col-span-2" delay={0.1}>
          <div className="bg-spa-muted rounded-3xl p-7 sm:p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-spa-sage mb-5">
              Key Benefits
            </p>
            <ul className="space-y-3.5">
              {service.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-spa-slate">
                  <Check className="w-4 h-4 mt-1 text-spa-terracotta shrink-0" />
                  <span className="text-sm sm:text-base leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* OILS MENU (if present) */}
      {service.oilsMenu && (
        <section className="bg-spa-muted py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-spa-terracotta mb-4">
                Essential Oil Menu
              </p>
            </Reveal>
            <RevealGroup className="flex flex-wrap gap-4 mt-4">
              {service.oilsMenu.map((oil) => (
                <RevealItem key={oil}>
                  <span
                    data-testid={`oil-${oil.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-block bg-spa-surface border border-spa-slate/10 rounded-full px-6 py-3 text-spa-slate font-serif text-lg"
                  >
                    {oil}
                  </span>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>
        </section>
      )}

      {/* SESSION PROCESS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-spa-terracotta mb-4">
            Your Session
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-spa-slate max-w-xl">
            What to expect
          </h2>
        </Reveal>

        <RevealGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {service.process.map((step, i) => (
            <RevealItem key={step.title}>
              <div
                data-testid={`process-step-${i}`}
                className="bg-spa-surface border border-spa-slate/5 rounded-3xl p-7 h-full"
              >
                <span className="font-serif text-3xl text-spa-sage">
                  0{i + 1}
                </span>
                <h3 className="font-serif text-lg text-spa-slate mt-4">
                  {step.title}
                </h3>
                <p className="text-sm text-spa-textMuted mt-2 leading-relaxed">
                  {step.text}
                </p>
              </div>
            </RevealItem>
          ))}
        </RevealGroup>
      </section>

      {/* DURATIONS + CTA */}
      <section className="bg-spa-slate py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-spa-sage mb-4">
              Session Duration
            </p>
            <div
              className="flex flex-wrap justify-center gap-4 mt-2"
              data-testid="duration-options"
            >
              {service.durations.map((d) => (
                <span
                  key={d}
                  className="flex items-center gap-2 border border-spa-cream/20 text-spa-cream px-6 py-3 rounded-full text-sm font-medium"
                >
                  <Clock className="w-3.5 h-3.5" /> {d}
                </span>
              ))}
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl text-spa-cream mt-12">
              Book this therapy
            </h2>
            <p className="text-spa-cream/70 mt-3 max-w-md mx-auto">
              Call us directly for current pricing & availability — no forms
              needed.
            </p>
            <a
              href={`tel:${BUSINESS.phoneClean}`}
              data-testid="service-cta-call-button"
              className="inline-flex items-center gap-2.5 bg-spa-terracotta text-spa-cream px-8 py-4 rounded-full text-sm font-medium mt-8 hover:bg-spa-cream hover:text-spa-slate transition-all duration-300"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
            </a>
          </Reveal>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-spa-terracotta mb-4">
            Explore More
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl text-spa-slate max-w-xl">
            Other therapies you may love
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
          {otherServices.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08}>
              <Link
                to={s.path}
                data-testid={`related-service-${s.slug}`}
                className="group relative block rounded-3xl overflow-hidden aspect-[4/5]"
              >
                <img
                  src={s.cardImage}
                  alt={`${s.name} at Blossom Spa Madhapur`}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-spa-slate/90 via-spa-slate/10 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-6">
                  <h3 className="font-serif text-xl text-spa-cream">
                    {s.name}
                  </h3>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-spa-blush text-sm group-hover:gap-3 transition-all duration-300">
                    Learn More <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
};
